import { keyBy } from 'lodash-es'
import { useMemo } from 'react'

import { useProduct } from '../application/product/ProductProvider'
import { ExcessLine, getActiveCoverLevelOption, Quote } from '../domain'
import { ExcessCombination, getCoverOptionConfig } from '../state/configuration'

interface Excesses {
  main: ExcessLine[]
  other: ExcessLine[]
  voluntary: Array<{
    voluntaryExcessCode: string
    excesses?: number[]
    excess: number
  }>
  combinations: ExcessCombination[] | undefined
}

export const isVoluntaryExcess =
  (voluntaryExcessCode: string) => (voluntary: Excesses['voluntary'][0]) =>
    voluntary.voluntaryExcessCode === voluntaryExcessCode

export const useExcesses = (quote: Quote | undefined): Excesses => {
  const { product } = useProduct()

  return useMemo(() => {
    const coverLevelOption = getActiveCoverLevelOption(quote)
    const coverOptionConfig = getCoverOptionConfig(product, coverLevelOption?.coverLevel)

    const excessesByCategory = keyBy<ExcessLine>(
      quote?.excessLines ?? [],
      'excessCategory',
    )

    const main = product.excess?.mainExcesses
      ?.map((it) => excessesByCategory[it])
      ?.filter(Boolean)
      ?.filter((it) => it.mandatoryExcess > 0)

    const other = product.excess?.otherExcesses
      ?.map((it) => excessesByCategory[it])
      ?.filter(Boolean)

    const voluntary = product.excess?.voluntaryExcesses?.map(
      ({ voluntaryExcessCode, excesses }) =>
        ({
          voluntaryExcessCode,
          excesses,
          excess: quote?.voluntaryExcesses[voluntaryExcessCode] ?? 0,
        } satisfies Excesses['voluntary'][0]),
    )

    return {
      main: main ?? [],
      other: other ?? [],
      voluntary: voluntary ?? [],
      combinations: coverOptionConfig?.excesses?.combinations,
    }
  }, [product, quote])
}
